import React, { useState, useMemo, type FC, useEffect } from 'react';
import type { CSSObjectWithLabel } from 'react-select';
import { defineMessages, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { isExternalUser, isUser, type ActionTypes, type Value } from '@atlaskit/user-picker';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';

import { useThirdPartyIntegrations } from '@atlassian/integrations/third-party';

import { fg } from '@confluence/feature-gating';
import { useTeamPicker } from '@confluence/permissions-principals/entry-points/useTeamPicker.ts';
import { UserAndGroupSearchPicker } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker.ts';
import { expVal, expValEquals, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';

import type {
	UserPickerPrincipal,
	UserPickerUserPrincipal,
	StagedPrincipalsAddMode,
	UserPickerEmailPrincipal,
	NatureOfCta,
} from '../../commonTypes.tsx';
import type { Actions } from '../../../controller/actions.ts';
import type { DerivedState } from '../../../controller/derivedState.ts';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller.tsx';
import { useInviteCapability } from '../../../controller/queries/useInviteCapability.tsx';

import { InviteMessage } from './InviteMessage.tsx';
import { SuggestedShareUsers } from './SuggestedShareUsers.tsx';

const i18n = defineMessages({
	canView: {
		id: 'share-and-restrict-dialog.principals-stager.can-view',
		defaultMessage: 'Can view',
		description:
			'Label for the view option in the permissions dropdown in the principals stager section',
	},
	canEdit: {
		id: 'share-and-restrict-dialog.principals-stager.can-edit',
		defaultMessage: 'Can edit',
		description:
			'Label for the edit option in the permissions dropdown in the principals stager section',
	},
	placeholder: {
		id: 'share-and-restrict-dialog.principals-stager.placeholder',
		defaultMessage: 'Add names, groups, or emails',
		description: 'Placeholder text for a stager without principals.',
	},
	placeholderWithTeams: {
		id: 'share-and-restrict-dialog.principals-stager.placeholder-with-teams',
		defaultMessage: 'Add names, teams, groups, or emails',
		description: 'Placeholder text for a stager without principals.',
	},
	capacityExceeded: {
		id: 'share-and-restrict-dialog.permissions-select.capacity-exceeded',
		defaultMessage: 'Maximum capacity of 200 users and 200 groups exceeded.',
		description: 'Inline text describing a capacity of users and groups, the values are unchanging',
	},
	capacityExceededWithTeams: {
		id: 'share-and-restrict-dialog.permissions-select.capacity-exceeded-with-teams',
		defaultMessage: 'Maximum capacity of 200 users and 200 teams or groups exceeded.',
		description:
			'Inline text describing a capacity of users and teams or groups, the values are unchanging',
	},
	userLimitExceeded: {
		id: 'share-and-restrict-dialog.permissions-select.user-limit-exceeded',
		defaultMessage:
			'Please remove {userOverflow, plural, one {# user} other {# users}} to continue.',
		description: 'Inline text instructing the user to remove users to proceed.',
	},
	groupLimitExceeded: {
		id: 'share-and-restrict-dialog.permissions-select.group-limit-exceeded',
		defaultMessage:
			'Please remove {groupOverflow, plural, one {# group} other {# groups}} to continue.',
		description: 'Inline text instructing the user to remove groups to proceed,',
	},
	groupTeamLimitExceeded: {
		id: 'share-and-restrict-dialog.permissions-select.group-team-limit-exceeded',
		defaultMessage:
			'Please remove {groupOverflow, plural, one {# team or group} other {# teams or groups}} to continue.',
		description: 'Inline text instructing the user to remove teams or groups to proceed,',
	},
	userAndGroupLimitExceededUser: {
		id: 'share-and-restrict-dialog.permissions-select.user-and-group-limit-exceeded-prefix',
		defaultMessage: 'Please remove {userOverflow, plural, one {# user} other {# users}}',
		description:
			'Inline text instructing the user to remove users to proceed, will be concatenated and placed before share-and-restrict-dialog.permissions-select.user-and-group-limit-exceeded-suffix',
	},
	userAndGroupLimitExceededGroup: {
		id: 'share-and-restrict-dialog.permissions-select.user-and-group-limit-exceeded-suffix',
		defaultMessage: 'and {groupOverflow, plural, one {# group} other {# groups}} to continue.',
		description:
			'Inline text instructing the user to remove groups to proceed, will be concatenated and placed after share-and-restrict-dialog.permissions-select.user-and-group-limit-exceeded-prefix',
	},
	userAndGroupTTeamLimitExceededGroup: {
		id: 'share-and-restrict-dialog.permissions-select.user-and-group-team-limit-exceeded-suffix',
		defaultMessage:
			'and {groupOverflow, plural, one {# team or group} other {# teams or groups}} to continue.',
		description:
			'Inline text instructing the user to remove teams or groups to proceed, will be concatenated and placed after share-and-restrict-dialog.permissions-select.user-and-group-limit-exceeded-prefix',
	},
	shareButton: {
		id: 'share-and-restrict-dialog.principal-stager.share-cta',
		defaultMessage: 'Share',
		description: 'Button text for the share button for share and restrict dialog',
	},
	addButton: {
		id: 'share-and-restrict-dialog.principal-stager.add-cta',
		defaultMessage: 'Add',
		description: 'Button text for the add button for staged permissions flow',
	},
	saveButton: {
		id: 'share-and-restrict-dialog.principal-stager.save-cta',
		defaultMessage: 'Save',
		description: 'Button text for the Save button for share and restrict dialog',
	},
	sendButton: {
		id: 'share-and-restrict-dialog.principal-stager.send-cta',
		defaultMessage: 'Send',
		description:
			'Button text for the Send button for share and restrict dialog when user can only notify',
	},
});

export type PrincipalsStagerDropdownType = 'can-view' | 'can-edit' | 'hidden';

export type PrincipalCounts = {
	users: number;
	groups: number;
};

export type PrincipalsStagerProps = {
	isLockedOut: boolean;
	principals: UserPickerPrincipal[];
	exceededLimit: DerivedState['exceededLimit'];
	dropdown: PrincipalsStagerDropdownType;
	onChangePrincipals: (newPrincipals: UserPickerPrincipal[]) => void;
	onSuggestedUserClick?: () => void;
	onChangeDropdown: (
		newAction: StagedPrincipalsAddMode,
		principalsStagerPrincipals: UserPickerPrincipal[],
	) => void;
	isDropdownEditable: boolean;
	specificAccessIds: string[];
	isSharingToEmailsDisabled: boolean;
	contentId: string;
	defaultSuggestedUsers?: UserPickerUserPrincipal[];
	currentUserEmailDomain?: string;
	markPrincipalsAsInvited: Actions['markPrincipalsAsInvited'];
	markPrincipalsAsSkipped: Actions['markPrincipalsAsSkipped'];
	handleCtaClick: () => void;
	/** Staged-mode Add button action (publish modal flow) */
	onAddClick: () => void;
	isCtaInProgress: boolean;
	charLimitExceeded: boolean;
	natureOfCta: NatureOfCta;
	shouldShowStagedCta: boolean;
	shouldShowLiveCta: boolean;
};

const errorText = xcss({
	color: 'color.text.danger',
});

const dropdownContainer = xcss({
	width: '100%',
	minWidth: '112px',
});

const principalsStagerDropdownContainer = xcss({
	display: 'flex',
	flexShrink: 0,
});

const uneditableDropdownContainer = xcss({
	whiteSpace: 'nowrap',
	border: `${token('border.width')} solid ${token('color.border.input')}`,
	borderRadius: 'radius.small',
	paddingLeft: 'space.100',
	paddingRight: 'space.500',
	paddingBlock: 'space.100',
	lineHeight: '22px',
	height: '32px',
	display: 'flex',
	alignItems: 'center',
});

const stagerContainer = xcss({
	flexGrow: 1,
});

const noOptionsStyle = xcss({});

const PrincipalsStagerDropdown: FC<{
	isLockedOut: boolean;
	dropdown: Extract<PrincipalsStagerDropdownType, 'can-view' | 'can-edit'>;
	onChangeDropdown: PrincipalsStagerProps['onChangeDropdown'];
	isEditable: boolean;
	principals: UserPickerPrincipal[];
}> = ({ isLockedOut, dropdown, onChangeDropdown, isEditable, principals }) => {
	const { formatMessage } = useIntl();
	const options = [
		{
			label: formatMessage(i18n.canView),
			value: 'can-view',
		},
		{
			label: formatMessage(i18n.canEdit),
			value: 'can-edit',
		},
	];

	const value = options[dropdown === 'can-view' ? 0 : 1];

	return (
		<Box xcss={principalsStagerDropdownContainer}>
			{isEditable ? (
				<Box xcss={dropdownContainer}>
					<Select
						isDisabled={isLockedOut}
						options={options}
						isSearchable={false}
						value={value}
						onChange={(option) =>
							onChangeDropdown(option === options[0] ? 'as-viewer' : 'as-editor', principals)
						}
						spacing={
							expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
								? 'compact'
								: 'default'
						}
					/>
				</Box>
			) : (
				<Box xcss={uneditableDropdownContainer}>{value.label}</Box>
			)}
		</Box>
	);
};

export const getMenuListStyle =
	(shouldShowInlineInviteStyles: boolean) => (provided: CSSObjectWithLabel, state: any) => {
		if (state?.options.length === 0 && shouldShowInlineInviteStyles) {
			return {
				...provided,
				display: 'none',
			};
		}
		return provided;
	};

export const getMultiValueStyle = (
	provided: CSSObjectWithLabel,
	state: any,
	isSharingToEmailsDisabled: boolean,
	shouldShowInviteMessage: boolean,
) => {
	const { data: dataObject } = state;
	const isAEmail = dataObject.data.type === 'email';

	return {
		...provided,
		borderColor:
			isSharingToEmailsDisabled && shouldShowInviteMessage && isAEmail
				? token('color.border.warning')
				: provided.borderColor,
	};
};

export const PrincipalsStager: FC<PrincipalsStagerProps> = ({
	isLockedOut,
	principals,
	exceededLimit,
	dropdown,
	isDropdownEditable,
	onChangePrincipals,
	onSuggestedUserClick,
	onChangeDropdown,
	specificAccessIds,
	isSharingToEmailsDisabled,
	contentId,
	defaultSuggestedUsers = [],
	currentUserEmailDomain,
	markPrincipalsAsInvited,
	markPrincipalsAsSkipped,
	handleCtaClick,
	onAddClick,
	isCtaInProgress,
	charLimitExceeded,
	natureOfCta,
	shouldShowStagedCta,
	shouldShowLiveCta,
}) => {
	const { formatMessage } = useIntl();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [resolvedPrincipals, setResolvedPrincipals] = useState<
		React.ComponentProps<typeof InviteMessage>['resolvedPrincipals']
	>({ invited: [], skipped: [], all: [] });
	const { isLimitExceeded, userOverFlow, groupOverFlow } = exceededLimit;
	const { cloudId } = useSessionData();
	const { pickerProps: teamPickerProps } = useTeamPicker();
	const {
		canUserInvite,
		loadInviteCapabilities,
		called: inviteCapabilitiesHasBeenCalled,
	} = useInviteCapability();
	const { thirdPartyResolver, initThirdPartyIntegrations, isInitialized, isInitializing } =
		useThirdPartyIntegrations({
			cloudId,
			createAnalyticsEvent,
			canUserInvite,
		});
	const [suggestEmailsForDomain, setSuggestEmailsForDomain] = useState<string>();
	const ctaOptions: Record<'share' | 'save' | 'send', MessageDescriptor> = {
		share: i18n.shareButton,
		save: i18n.saveButton,
		send: i18n.sendButton,
	};
	const getCtaLabel = (cta: NatureOfCta): MessageDescriptor => {
		if (cta === 'share' || cta === 'save' || cta === 'send') {
			return ctaOptions[cta];
		}
		// Fallback to share button for 'none' (should not happen in practice)
		return i18n.shareButton;
	};

	const renderPrimaryCtaButton = () => {
		if (shouldShowLiveCta) {
			return (
				<Button
					appearance="primary"
					onClick={handleCtaClick}
					isLoading={isCtaInProgress}
					isDisabled={
						(!isCtaInProgress && isLockedOut) ||
						charLimitExceeded ||
						isLimitExceeded ||
						isSharingToEmailsDisabled
					}
				>
					{formatMessage(getCtaLabel(natureOfCta))}
				</Button>
			);
		}

		if (shouldShowStagedCta) {
			return (
				<Button appearance="primary" onClick={onAddClick}>
					{formatMessage(i18n.addButton)}
				</Button>
			);
		}

		return null;
	};

	const getLimitOverflowMessage = (
		userOverflow: PrincipalsStagerProps['exceededLimit']['userOverFlow'],
		groupOverflow: PrincipalsStagerProps['exceededLimit']['groupOverFlow'],
	) => {
		if (userOverflow && groupOverflow) {
			// This needs to be concatenated due to double pluralization
			return `${formatMessage(i18n.userAndGroupLimitExceededUser, { userOverflow })} ${formatMessage(
				teamPickerProps.includeTeamsUpdates
					? i18n.userAndGroupTTeamLimitExceededGroup
					: i18n.userAndGroupLimitExceededGroup,
				{ groupOverflow },
			)}`;
		}
		if (userOverflow) {
			return formatMessage(i18n.userLimitExceeded, { userOverflow });
		}
		return formatMessage(
			teamPickerProps.includeTeamsUpdates ? i18n.groupTeamLimitExceeded : i18n.groupLimitExceeded,
			{ groupOverflow },
		);
	};

	useEffect(() => {
		if (isLimitExceeded) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'exceededLimit',
					action: 'warning',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						isLimitExceeded,
						userOverFlow: Boolean(userOverFlow),
						groupOverFlow: Boolean(groupOverFlow),
						contentId,
					},
				},
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLimitExceeded, userOverFlow, groupOverFlow]);

	const userEmails = useMemo(() => {
		const allResolvedEmails: Set<string> = new Set(
			resolvedPrincipals.all.map((email) => email.email),
		);
		return principals.reduce<string[]>((acc, principal) => {
			switch (principal.type) {
				case 'email':
					acc.push(principal.id);
					return acc;
				case 'user':
					if (principal.email && allResolvedEmails.has(principal.email)) {
						acc.push(principal.email);
					}
					return acc;
				case 'external_user':
					if (principal.email) {
						acc.push(principal.email);
					}
					return acc;
				default:
					return acc;
			}
		}, []);
	}, [principals, resolvedPrincipals]);

	const areEmailsPresent = userEmails.length > 0;
	const shouldShowInviteMessage =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-444-invite-usd-improvements-m2-gate') &&
		['control', 'variation'].includes(
			UNSAFE_expValNoExposure('twcg-444-invite-usd-improvements-m2', 'cohort', 'not-enrolled'),
		)
			? areEmailsPresent && canUserInvite()
			: areEmailsPresent;

	const hasDropdown = dropdown !== 'hidden';

	const userPickerWidth = (() => {
		if (hasDropdown) {
			return '436px';
		}
		return '552px';
	})();

	const handleSuggestedUserClick = (selectedUser: UserPickerUserPrincipal) => {
		onChangePrincipals([...principals, selectedUser]);
		onSuggestedUserClick?.();
	};

	const currentPickedPrincipalIds: string[] = useMemo(() => {
		const principalIds = principals.filter(({ type }) => type === 'user').map(({ id }) => id);
		return [...new Set([...principalIds, ...specificAccessIds])];
	}, [principals, specificAccessIds]);

	const shouldShowInlineInviteStyles =
		shouldShowInviteMessage &&
		isMenuOpen &&
		expValEquals('twcg-444-invite-usd-improvements-m2', 'cohort', 'variation');

	const multiValueStyleFn = (provided: CSSObjectWithLabel, state: any) =>
		getMultiValueStyle(provided, state, isSharingToEmailsDisabled, shouldShowInviteMessage);

	const onFocus = () => {
		if (shouldShowInviteMessage) setIsMenuOpen(true);

		if (!fg('twcg-444-invite-usd-improvements-m2-gate')) return;

		if (
			!inviteCapabilitiesHasBeenCalled &&
			['control', 'variation'].includes(
				expVal('twcg-444-invite-usd-improvements-m2', 'cohort', 'not-enrolled'),
			)
		) {
			loadInviteCapabilities();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'principalStager',
					action: 'focused',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						inviteImprovementsM2Gate: fg('twcg-444-invite-usd-improvements-m2-gate'),
						inviteImprovementsM2Cohort: expVal(
							'twcg-444-invite-usd-improvements-m2',
							'cohort',
							'not-enrolled',
						),
					},
				},
			}).fire();
		}

		if (!expValEquals('twcg-444-invite-usd-improvements-m2', 'cohort', 'variation')) return;

		if (!isInitialized && !isInitializing) {
			void initThirdPartyIntegrations();
		}

		if (!suggestEmailsForDomain) {
			setSuggestEmailsForDomain(currentUserEmailDomain);
		}
	};

	const onSelectOption = (value: Value) => {
		setIsMenuOpen(shouldShowInviteMessage);

		if (value && Array.isArray(value)) {
			const selected = value[value.length - 1] as UserPickerUserPrincipal;
			if (selected && selected.type === 'external_user') {
				setIsMenuOpen(true);

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'option',
						action: 'selected',
						actionSubjectId: 'externalUser',
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						attributes: {
							canUserInvite: canUserInvite(),
							inviteImprovementsM2Cohort: expVal(
								'twcg-444-invite-usd-improvements-m2',
								'cohort',
								'not-enrolled',
							),
						},
					},
				}).fire();
			}
		}
	};

	const onCreateOption = (value: Value) => {
		if (value && Array.isArray(value)) {
			const created = value[value.length - 1] as UserPickerEmailPrincipal;
			const domain = created.id.split('@')[1];

			// if the created email is already invited, mark it as invited and convert it to `user` principals
			const invitedPrincipal = resolvedPrincipals.invited.find(
				(email) => email.email === created.id,
			);
			if (created.type === 'email' && invitedPrincipal) {
				markPrincipalsAsInvited([invitedPrincipal], []);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'option',
					action: 'created',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						canUserInvite: canUserInvite(),
						isDefaultDomain: !!suggestEmailsForDomain && domain === suggestEmailsForDomain,
						inviteImprovementsM2Cohort: expVal(
							'twcg-444-invite-usd-improvements-m2',
							'cohort',
							'not-enrolled',
						),
					},
				},
			}).fire();
		}
	};

	const onRemoveValue = (value: Value) => {
		if (value && Array.isArray(value)) {
			const removed = principals.find((principal) => !value.find((v) => v.id === principal.id));
			if (
				removed?.type !== 'email' &&
				removed?.type !== 'user' &&
				removed?.type !== 'external_user'
			)
				return;
			const email = removed.type === 'email' ? removed.id : removed.email;
			const domain = email?.split('@')[1];
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'option',
					action: 'removed',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						userType: removed.type,
						canUserInvite: canUserInvite(),
						wasUserInvited:
							!!email &&
							!!resolvedPrincipals.invited.find((principal) => principal.email === email),
						isDefaultDomain: !!suggestEmailsForDomain && domain === suggestEmailsForDomain,
						inviteImprovementsM2Cohort: expVal(
							'twcg-444-invite-usd-improvements-m2',
							'cohort',
							'not-enrolled',
						),
					},
				},
			}).fire();
		}
	};

	return (
		<Stack space="space.075">
			<Inline grow="fill" space="space.075" alignBlock="stretch">
				<Box xcss={stagerContainer}>
					<UserAndGroupSearchPicker
						ariaLabel={formatMessage(
							teamPickerProps.includeTeamsUpdates ? i18n.placeholderWithTeams : i18n.placeholder,
						)}
						smart
						open={isMenuOpen}
						onInputChange={() => setIsMenuOpen(true)}
						// The onBlur event ensures the user picker closes when it loses focus.
						// Without onBlur, the user picker would stay open due to onInputChange keeping it open,
						// and would only close when a user is selected.
						onBlur={() => setIsMenuOpen(false)}
						// We want to open the menu to see the Invite flow if there are emails present
						// when the user clicks into the User Picker
						onFocus={onFocus}
						isDisabled={isLockedOut}
						fieldId="share-and-restrict-dialog:user-and-group-search:user-and-group-picker"
						value={principals}
						onChange={
							// In practice, the user picker always returns OptionData[] for
							// this configuration, but TypeScript doesn't know that, so handle
							// all cases
							(value: Value, action: ActionTypes) => {
								if (!value) onChangePrincipals([]);
								else if (Array.isArray(value)) onChangePrincipals(value as UserPickerPrincipal[]);
								else onChangePrincipals([value as UserPickerPrincipal]);

								switch (action) {
									case 'select-option':
										onSelectOption(value);
										break;
									case 'create-option':
										onCreateOption(value);
										break;
									case 'remove-value':
										onRemoveValue(value);
										break;
									default:
										break;
								}
							}
						}
						// Prevent the 'Escape' key from closing the dialog while the user picker has focus
						onKeyDown={(e: React.KeyboardEvent<Element>) => {
							if (e.key === 'Escape') {
								e.stopPropagation();
							}
						}}
						// Ensures the menu closes after pressing 'Escape'
						onClose={() => setIsMenuOpen(false)}
						// Display nothing if no results unless an email is added to the user picker.
						// A noOptionsMessage must be present in order to show the InviteMessage header
						noOptionsMessage={() =>
							shouldShowInviteMessage ? <Box xcss={noOptionsStyle} /> : null
						}
						header={
							shouldShowInviteMessage ? (
								<InviteMessage
									userEmails={userEmails}
									showEmailWarningMessage={isSharingToEmailsDisabled}
									resolvedPrincipals={resolvedPrincipals}
									setResolvedPrincipals={setResolvedPrincipals}
									markPrincipalsAsInvited={markPrincipalsAsInvited}
									markPrincipalsAsSkipped={markPrincipalsAsSkipped}
								/>
							) : null
						}
						// suppress default 'add more users' prompt
						addMoreMessage=""
						includeGroup
						isMulti
						placeholder={formatMessage(
							teamPickerProps.includeTeamsUpdates ? i18n.placeholderWithTeams : i18n.placeholder,
						)}
						width="100%"
						allowEmail
						displayEmailInByline={expVal(
							'confluence-enable-email-ux-in-permissions-surfaces',
							'isEnabled',
							false,
						)}
						suggestEmailsForDomain={suggestEmailsForDomain}
						enableEmailSearch={expVal(
							'confluence-enable-email-ux-in-permissions-surfaces',
							'isEnabled',
							false,
						)}
						allowEmailSelectionWhenEmailMatched={
							!expVal('confluence-enable-email-ux-in-permissions-surfaces', 'isEnabled', false)
						}
						styles={{
							control: (provided: CSSObjectWithLabel) => ({
								...provided,
								...(exceededLimit.isLimitExceeded && {
									border: `${token('border.width.selected')} solid ${token('color.border.warning')}`,
								}),
								...(expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
									? {
											minHeight: '30px',
										}
									: {
											minHeight: '40px',
										}),
								maxHeight: '117px',
								maxWidth: userPickerWidth,
								overflowY: 'auto',
								overflowX: 'auto',
							}),
							valueContainer: (provided: CSSObjectWithLabel) => ({
								...provided,
								...(expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
									? {
											paddingTop: token('space.025'),
											paddingBottom: token('space.0'),
										}
									: {
											paddingTop: token('space.025'),
											paddingBottom: token('space.025'),
										}),
							}),
							multiValue: multiValueStyleFn,
							menuList: getMenuListStyle(shouldShowInlineInviteStyles),
						}}
						includeTeams={teamPickerProps.includeTeams}
						includeTeamsUpdates={teamPickerProps.includeTeamsUpdates}
						verifiedTeams={teamPickerProps.verifiedTeams}
						transformOptions={(options) => {
							const allUserEmails = new Set(
								[...options, ...principals].filter(isUser).map((item) => item.email),
							);

							const filteredOptions = options.filter((option) => {
								if (isExternalUser(option)) {
									return option.email && !allUserEmails.has(option.email);
								}
								return true;
							});

							return Promise.resolve(filteredOptions);
						}}
						userResolvers={[thirdPartyResolver]}
					/>
				</Box>
				{hasDropdown && (
					<PrincipalsStagerDropdown
						isLockedOut={isLockedOut}
						dropdown={dropdown}
						onChangeDropdown={onChangeDropdown}
						isEditable={isDropdownEditable}
						principals={principals}
					/>
				)}
				{expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') &&
					renderPrimaryCtaButton()}
			</Inline>
			<SuggestedShareUsers
				currentPickedPrincipalIds={currentPickedPrincipalIds}
				onSuggestedUserClick={handleSuggestedUserClick}
				defaultSuggestedUsers={defaultSuggestedUsers}
			/>
			{isLimitExceeded && (
				<Inline space="space.075" alignBlock="center">
					<StatusErrorIcon label="" color={token('color.icon.danger')} />
					<Box xcss={errorText}>
						<Stack>
							<Box>
								{formatMessage(
									teamPickerProps.includeTeamsUpdates
										? i18n.capacityExceededWithTeams
										: i18n.capacityExceeded,
								)}
							</Box>
							<Box>{getLimitOverflowMessage(userOverFlow, groupOverFlow)}</Box>
						</Stack>
					</Box>
				</Inline>
			)}
			{shouldShowLiveCta &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') && (
					<ShortcutListener
						accelerator={['command+enter', 'ctrl+enter']}
						listener={handleCtaClick}
						allowInDialog
					/>
				)}
		</Stack>
	);
};
